<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("supplier_due_entry") }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/supplier/due/list" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="submitSupplierPayment" enctype="multipart/form-data">
        <!-- Header Form Section -->
        <div class="row gy-2 gx-4 mb-3 justify-content-between align-items-center">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:150px;">{{$t('voucher_no')}}:</label>
            <input type="text" class="form-control" v-model="supplier.receiving_no" readonly/>
          </div>

          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:90px;">{{$t('voucher_date')}}:</label>
            <input type="date" class="form-control" v-model="supplier.receiving_date" @change="fetchReceivingNo" />
            <div v-if="errors.receiving_date" class="text-danger mt-2">
              <span>{{errors.receiving_date}}</span>
            </div>
          </div>

          <div class="col-md-6 d-flex align-items-start">
            <label class="form-label small mb-0 me-3" style="width:107px;">{{$t('select_supplier')}}:</label>
            <div class="flex-grow-1 position-relative">
              <!-- Search Input -->
              <input
                type="text"
                v-model="query"
                @input="searchSuppliers"
                :placeholder="$t('search_supplier_by_name_phone')"
                class="form-control"
                @keydown="handleKeys"
              />

              <!-- Dropdown Search Results -->
              <div
                v-if="query"
                class="search-results shadow rounded bg-white position-absolute mt-1"
                style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
              >
                <!-- Results List -->
                <ul class="list-group list-group-flush mb-0">
                  <li
                    v-for="(supplier, index) in results"
                    :key="supplier.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'bg-primary text-white': index === supplierIndex }"
                    style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                    @click="selectSupplier(supplier)"
                  >
                    <div class="flex-grow-1">
                      <strong>{{ supplier.name }}</strong> - {{ supplier.phone_number }}
                    </div>
                  </li>
                </ul>

                <!-- No Results -->
                <div v-if="!results.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                  No results found
                </div>
              </div>
            </div>
          </div>

          <!-- Selected Supplier Info -->
          <div class="col-md-6 d-flex align-items-center" v-if="selectedSupplier">
            <label class="form-label small mb-0 me-2">Supplier Info:</label>
            <span style="font-size:1.1rem;">{{ selectedSupplier.name }}</span>
            <span style="font-size:1.1rem; margin-right:.5rem;">[{{ selectedSupplier.phone_number }}]</span>
            <span
              style="font-size:1.1rem;"
            >
              [<strong>Due:</strong>
              <span :style="{ color: selectedSupplier.due_balance > 0 ? 'red' : 'green' }">
                ৳{{ selectedSupplier.due_balance || 0 }}
              </span>]
            </span>
          </div>
        </div>

        <div class="row gy-2 gx-4 mb-3 p-3 mx-1 border rounded shadow-sm align-items-start">
          <!-- Narration -->
          <div class="col-md-6 d-flex mb-2">
            <label class="form-label small mb-0 me-3" style="width:75px; flex-shrink:0;">
              {{$t('narration')}}: <span class="text-danger">*</span>
            </label>
            <div class="flex-grow-1">
              <input
                type="text"
                class="form-control"
                placeholder="Narration"
                v-model="supplier.narration"
              />
              <div class="text-danger" style="height:1.2em; font-size:0.875rem;">
                <span v-if="errors.narration">{{ errors.narration }}</span>
              </div>
            </div>
          </div>

          <!-- Amount -->
          <div class="col-md-6 d-flex mb-2">
            <label class="form-label small mb-0 me-3" style="width:73px; flex-shrink:0;">
              {{$t('amount')}}:
            </label>
            <div class="flex-grow-1 d-flex flex-column">
              <div class="d-flex">
                <input
                  type="number"
                  class="form-control me-2"
                  placeholder="Amount"
                  v-model="supplier.amount"
                />
                <button type="button" class="btn btn-info" @click="addSupplier">
                  {{$t('add')}}
                </button>
              </div>
              <div class="text-danger" style="height:1.2em; font-size:0.875rem;">
                <span v-if="errors.amount">{{ errors.amount }}</span>
              </div>
            </div>
          </div>
        </div>


        <div class="table-responsive mb-3 fixed-table">
          <table class="table table-bordered table-striped align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 30%">{{$t('supplier_name')}}</th>
                <th style="width: 30%">{{$t('narration')}}</th>
                <th style="width: 20%">{{$t('amount')}}</th>
                <th style="width: 20%" >{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cust, index) in addedSuppliers" :key="index">
                <td>{{ cust.supplier_name }}</td>
                <td>{{ cust.narration }}</td>
                <td>{{ cust.amount }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeSupplier(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <!-- Show empty row if no data -->
              <tr v-if="!addedSuppliers.length">
                <td colspan="11" class="text-center text-muted">No supplier due added</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment Section -->
        <div class="d-flex justify-content-between align-items-start mt-4">
          <div class="d-flex gap-4">
            <div class="d-flex flex-column">
              <label class="form-label">
                {{$t('payment_type')}} <span class="text-danger">*</span>
              </label>
              <FormDropdown
                v-model="supplier.cash_type"
                :options="formattedcashtypes"
                style="width: 300px;"
              />
              <div v-if="errors.cash_type" class="text-danger">{{ errors.cash_type }}</div>
            </div>

            <!-- Bank Accounts -->
            <div v-if="isBank" class="d-flex flex-column">
              <label class="form-label">
                {{$t('bank_account')}} <span class="text-danger">*</span>
              </label>
              <FormDropdown
                v-model="supplier.bank_account"
                :options="formattedbankaccounts"
                style="width: 300px;"
              />
              <div v-if="errors.bank_account" class="text-danger">{{ errors.bank_account }}</div>
            </div>
          </div>

          <div class="d-flex flex-row gap-2 pe-2 mt-4">
            <button type="button" class="btn btn-cancel" @click="cancelForm">
              {{ $t('cancel') }}
            </button>

            <button type="submit" class="btn btn-submit" :disabled="saving">
              {{ $t('submit') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { todayLocal } from '@/utils';
import { apiErrorMessage } from '@/helpers/apiError';
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';

export default {
  components: {
    Vue3Datepicker,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      supplier: {
        supplier: null, narration: '', amount: '', cash_type: null,
        receiving_no: '', receiving_date: todayLocal(), bank_account: ''
      },
      cash_types: [],
      errors: {},
      errorMessage: "",
      saving: false,
      query: "",
      results: [],
      supplierIndex: 0,
      selectedSupplier: null,
      addedSuppliers: [],
      bank_accounts: [],
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchReceivingNo();
  },

  computed: {
    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    isBank() {
      const val = this.supplier.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },
  },

  methods: {
      async fetchDropdownData() {
        try {
          const [
            cash_typeRes, receive_noRes, bank_accRes,
          ] = await Promise.all([
            this.$axios.get(apiUrls.batch_cash_type.get),
            this.$axios.get(apiUrls.supplier_due_receive_number.get),
            this.$axios.get(apiUrls.batch_bank_account.get)
          ]);

          this.cash_types = cash_typeRes?.data || [];
          this.supplier.receiving_no = receive_noRes?.data?.receiving_no || '';
          this.bank_accounts = bank_accRes?.data || [];

          if (this.cash_types.length > 0 && !this.supplier.cash_type) {
            this.supplier.cash_type = this.cash_types[0].id;
          }

          if (this.bank_accounts.length > 0 && !this.supplier.bank_account) {
            this.supplier.bank_account = this.bank_accounts[0].id;
          }

        } catch (error) {
          console.error('Error loading dropdown data:', error);
        }
      },

      async searchSuppliers() {
        if (!this.query) {
          this.results = []
          return
        }

        try {
          const response = await this.$axios.get(apiUrls.supplier_search.get, {
            params: { q: this.query }
          })
          this.results = response.data.results;
          this.supplierIndex = 0;

          // render feather icons
          await this.$nextTick(() => {
            if (window.feather) feather.replace()
          })
        } catch (error) {
          console.error('Search error:', error)
          this.results = []
        }
      },

      async selectSupplier(supplier) {
        this.selectedSupplier = supplier
        try {
          const response = await this.$axios.get(`/supplier/due/${supplier.id}`);
          this.selectedSupplier.due_balance = response.data.due_balance;
        } catch (error) {
          console.error("Error fetching due balance:", error);
          this.selectedSupplier.due_balance = 0;
        }
        this.query = supplier.name
        this.supplier.supplier = supplier.id
        this.results = []
        this.supplierIndex = 0;
      },

      handleKeys(event) {
        if (event.key === 'ArrowDown') {
          this.supplierIndex = Math.min(this.supplierIndex + 1, this.results.length - 1);
          event.preventDefault();
        } else if (event.key === 'ArrowUp') {
          this.supplierIndex = Math.max(this.supplierIndex - 1, 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          const selectedProduct = this.results[this.supplierIndex];
          if (selectedProduct) {
            this.selectSupplier(selectedProduct);
          }
          event.preventDefault();
        }
      },

      addSupplier() {
        if (!this.selectedSupplier) {
          this.toast.info("Please select Supplier first.");
          return;
        }

        // Prevent duplicate supplier
        const exists = this.addedSuppliers.some(
          c => c.id === this.selectedSupplier.id
        );

        if (exists) {
          this.toast.info("Supplier already added.");
          return;
        }

        if (parseFloat(this.supplier.amount) > parseFloat(this.selectedSupplier.due_balance)) {
          this.toast.info("Payment amount cannot exceed supplier due amount");
          return;
        }

        // Frontend validation before adding to list
        this.errors = {};
        const requiredFields = [
          "supplier", "narration", "receiving_date", "amount"
        ];
        requiredFields.forEach(field => {
          if (!this.supplier[field]) this.errors[field] = this.$t('error.required');
        });

        if (Object.keys(this.errors).length > 0) {
          return;
        }

        // Store full supplier data in addedSuppliers
        const newSupplier = {
          ...this.supplier,
          id: this.selectedSupplier.id,
          supplier_name: this.selectedSupplier.name,
          supplier_phone: this.selectedSupplier.phone_number,
        };

        this.addedSuppliers.push(newSupplier);

        // reset only row fields
        this.supplier.supplier = null;
        this.supplier.narration = "";
        this.supplier.amount = "";

        // this.resetForm();
        this.errors = {};
        this.query = "";
        this.selectedSupplier = null;
      },

      async fetchReceivingNo() {
        try {
          let res = await this.$axios.get(apiUrls.supplier_due_receive_number.get, {
            params: { receiving_date: this.supplier.receiving_date }
          });
          this.supplier.receiving_no = res.data.receiving_no;
        } catch (err) {
          console.error(err);
        }
      },

      async submitSupplierPayment() {
        if (this.saving) return;
        if (!this.addedSuppliers.length) {
          this.toast.warning("Please add at least one Supplier before submitting.");
          return;
        }

        const cashType = this.selectedId(this.supplier.cash_type);
        const bankAccount = this.isBank ? this.selectedId(this.supplier.bank_account) : null;
        if (!cashType) {
          this.errors = { cash_type: this.$t('error.required') };
          return;
        }
        if (this.isBank && !bankAccount) {
          this.errors = { bank_account: this.$t('error.required') };
          return;
        }

        this.saving = true;
        try {
          // Payment type is chosen below the table, usually after the rows are added, so it is
          // taken from the form at submit time rather than from each row.
          const payload = this.addedSuppliers.map(row => ({
            ...row,
            cash_type: cashType,
            bank_account: bankAccount,
            receiving_no: this.supplier.receiving_no,
            receiving_date: this.supplier.receiving_date,
          }));
          await this.$axios.post(apiUrls.supplier_payment.post, payload);

          await this.fetchReceivingNo();
          const updatedReceivingNo = this.supplier.receiving_no;
          this.toast.success("Supplier payment saved successfully!");
          this.$router.push('/supplier/due/list');
          this.$emit("create_success");

          // Reset form after save
          this.supplier = {
            supplier: null, narration: '', amount: '', cash_type: this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '',
            receiving_no: updatedReceivingNo, receiving_date: this.supplier.receiving_date, bank_account: ''
          };
          this.selectedSupplier = null;
          this.addedSuppliers = [];

        } catch (error) {
          this.toast.error(apiErrorMessage(error));
          if (error.response?.data?.errors) {
            this.errors = error.response.data.errors;
          }
        } finally {
          this.saving = false;
        }
      },

      selectedId(value) {
        return value && typeof value === 'object' ? value.id : value;
      },

      cancelForm() {
        this.$router.push('/supplier/due/list');
      },

      removeSupplier(index) {
        this.addedSuppliers.splice(index, 1);
      },

      resetForm() {
        // Reset form fields and errors
        this.supplier = {
          supplier: '', narration: '', cash_type: this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '',
          receiving_no: '', receiving_date: '', bank_account: ''
        };
        this.errors = {};
        this.errorMessage = "";
      }
    },

  watch: {
    'supplier.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.supplier.receiving_no = '';
      }
    },

    'supplier.cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;
      if (String(id) === '1010102') {
        if (this.bank_accounts.length > 0) {
          this.supplier.bank_account = this.bank_accounts[0].id;
        }
      } else {
        this.supplier.bank_account = null;
      }
    },

    isBank(newVal) {
      if (!newVal) this.supplier.bank_account = null;
    },
  },
};
</script>


<style scoped>
/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

</style>

